import React, { useState, useRef, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import './Tooltip.scss'

interface TooltipProps {
  children: React.ReactNode
  content: string | React.ReactNode
  position?: 'top' | 'bottom' | 'left' | 'right'
  delay?: number
  className?: string
}

const Tooltip: React.FC<TooltipProps> = ({
  children,
  content,
  position = 'top',
  delay = 300,
  className = ''
}) => {
  const [isVisible, setIsVisible] = useState(false)
  const [actualPosition, setActualPosition] = useState(position)
  const timeoutRef = useRef<NodeJS.Timeout | null>(null)
  const triggerRef = useRef<HTMLDivElement>(null)
  const tooltipRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (isVisible && triggerRef.current && tooltipRef.current) {
      const triggerRect = triggerRef.current.getBoundingClientRect()
      const tooltipRect = tooltipRef.current.getBoundingClientRect()
      const viewportWidth = window.innerWidth
      const viewportHeight = window.innerHeight
      const padding = 10

      let newPosition = position

      // Check if tooltip would overflow viewport and adjust position
      switch (position) {
        case 'top':
          if (triggerRect.top - tooltipRect.height < padding) {
            newPosition = 'bottom'
          }
          break
        case 'bottom':
          if (triggerRect.bottom + tooltipRect.height > viewportHeight - padding) {
            newPosition = 'top'
          }
          break
        case 'left':
          if (triggerRect.left - tooltipRect.width < padding) {
            newPosition = 'right'
          }
          break
        case 'right':
          if (triggerRect.right + tooltipRect.width > viewportWidth - padding) {
            newPosition = 'left'
          }
          break
      }

      if (newPosition !== actualPosition) {
        setActualPosition(newPosition)
      }
    }
  }, [isVisible, position, actualPosition])

  const handleMouseEnter = () => {
    timeoutRef.current = setTimeout(() => {
      setIsVisible(true)
    }, delay)
  }

  const handleMouseLeave = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
    }
    setIsVisible(false)
    setActualPosition(position)
  }

  const getTooltipVariants = () => {
    const offset = 8
    const variants: any = {
      hidden: {
        opacity: 0,
        scale: 0.95
      },
      visible: {
        opacity: 1,
        scale: 1
      }
    }

    switch (actualPosition) {
      case 'top':
        variants.hidden.y = offset
        variants.visible.y = 0
        break
      case 'bottom':
        variants.hidden.y = -offset
        variants.visible.y = 0
        break
      case 'left':
        variants.hidden.x = offset
        variants.visible.x = 0
        break
      case 'right':
        variants.hidden.x = -offset
        variants.visible.x = 0
        break
    }

    return variants
  }

  return (
    <div 
      className={`tooltip-wrapper ${className}`}
      ref={triggerRef}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {children}
      
      <AnimatePresence>
        {isVisible && (
          <motion.div
            ref={tooltipRef}
            className={`tooltip tooltip-${actualPosition}`}
            initial="hidden"
            animate="visible"
            exit="hidden"
            variants={getTooltipVariants()}
            transition={{
              duration: 0.2,
              ease: [0.4, 0, 0.2, 1]
            }}
          >
            <div className="tooltip-content">
              {content}
            </div>
            {/* <div className="tooltip-arrow" /> */}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

export default Tooltip
